import React, { useState } from 'react'
import { Play } from 'lucide-react'
import VideoModal from './VideoModal'

const VideoCard: React.FC<{
    video: {
        title: string;
        url: string;
    }
}> = ({ video }) => {
    const [selectedVideo, setSelectedVideo] = useState<any>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Extract video ID from YouTube URL
    const getVideoId = (url: string): string => {
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
        const match = url.match(regex)
        return match?.[1] ?? ''
    }

    // Create video object from video data
    const createVideoObject = (videoData: { title: string; url: string }) => {
        const videoId = getVideoId(videoData.url)
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        const iframeUrl = `https://www.youtube.com/embed/${videoId}`

        return {
            videoId,
            thumbnailUrl,
            IframeUrl: iframeUrl,
            title: videoData.title,
            channelTitle: '',
            description: '',
            likeCount: 0,
            publishTime: ''
        }
    }

    const handleVideoClick = () => {
        const videoObject = createVideoObject(video)
        const url = new URL(videoObject.IframeUrl)
        url.searchParams.set('autoplay', '1')
        url.searchParams.set('enablejsapi', '1')

        setSelectedVideo({ ...videoObject, IframeUrl: url.toString() })
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedVideo(null)
    }

    const videoObject = createVideoObject(video)

    return (
        <>
            <div
                className="group relative cursor-pointer shrink-0 w-[240px] max-w-[260px] sm:w-[220px] sm:max-w-[240px]"
            >
                {/* Thumbnail Container with custom aspect ratio */}
                <div
                    className="relative w-full rounded-lg overflow-hidden"
                    style={{ aspectRatio: '16/9' }}
                >
                    {/* Thumbnail Image */}
                    <img
                        src={videoObject.thumbnailUrl}
                        alt={videoObject.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />

                    {/* Hover overlay with glassmorphism */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {/* Backdrop blur */}
                        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />

                        {/* Action buttons */}
                        <div className="absolute inset-0 flex items-center justify-center gap-3">
                            {/* Play button */}
                            <button
                                onClick={handleVideoClick}
                                className="p-2.5 bg-white/20 backdrop-blur-md rounded-full border border-white/30 hover:bg-white/30 transition-colors focus:outline-none"
                                aria-label="Play video"
                            >
                                <Play className="w-5 h-5 text-white fill-white" size={18} />
                            </button>

                        </div>
                    </div>

                    {/* Title on hover */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/70 via-black/40 to-transparent text-white text-sm font-semibold leading-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 whitespace-nowrap overflow-hidden text-ellipsis">
                        {video.title}
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            <VideoModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                video={selectedVideo}
            />
        </>
    )
}

export default VideoCard